// PerCompanyBentoTile — Bento 3x1 pro Firma (DrainX, G+K, InfraX, X-Holding).
//
// Radikal-Konsolidierung 03.07. (Kevin): Die Firmen-Kacheln sind das
// Zentrum des Cockpits. Jede zeigt EINE grosse echte Zahl (Umsatz YTD
// netto, live aus dem Tooltime-Snapshot) mit Count-up-Animation, darunter
// zwei Sekundaer-KPIs. Auch InfraX bekommt seine Zahl (r1_reference_only —
// Einzeldaten duerfen sichtbar sein, nur Konzern-AGGREGATE bleiben
// InfraX-frei, siehe cash/router R1-Korrektur 2026-05-20).
//
// Spec-Rule: KEINE Severity auf Firmen-Tiles (Firmen-Farbe only).

import type { EntityKpi } from "../../../lib/api";
import { formatEUR } from "../../../lib/api";
import type { FirmaSlug } from "../../../lib/firma-colors";
import { FIRMA_COLORS, FIRMA_LABEL } from "../../../lib/firma-colors";
import { formatEurRound, useCountUp } from "../../../hooks/useCountUp";
import { flashClass, flashStyle, useFlashOnChange } from "../../../hooks/useFlashOnChange";
import Sparkline from "../../charts/Sparkline";
import BahnStrecke from "../../leitstand/BahnStrecke";

/** Frische der Datenstrecken hinter dieser Kachel (Auftrag H Schritt 3). */
export type FirmaFrische = "ok" | "stale" | "missing";

// Dot-Farben = Signal-Semantik (ok/beobachten/handeln) — bewusst getrennt
// von der Firmen-Markenfarbe daneben.
const FRISCHE_DOT: Record<FirmaFrische, string> = {
  ok: "bg-signal-green/70",
  stale: "bg-signal-amber",
  missing: "bg-signal-clay",
};

interface Props {
  firma: FirmaSlug;
  /** Holding-KPIs (drainx, gundk, xholding). InfraX bleibt undefined. */
  data?: EntityKpi | null;
  /** Umsatz YTD netto (EUR, string) aus dem Tooltime-Snapshot — die eine
   *  grosse Zahl. Kommt fuer ALLE 4 Firmen inkl. InfraX. */
  umsatzYtdNettoEur?: string | null;
  /** 12M-BWA-Umsatz der Firma (bwaUmsatzSeriesForFirma) — einfarbige
   *  Sparkline im Firmen-Hex. Leer/undefined → keine Sparkline. */
  umsatz12m?: number[];
  /** Schlechtester Freshness-Status der Datenstrecken dieser Firma
   *  (tooltime/bwa). undefined/null → kein Dot. */
  frische?: FirmaFrische | null;
  /** Tooltip fuer den Frische-Dot (Datenart + Alter). */
  frischeHint?: string | null;
  /** AUFTRAG M B1: Pace-Mikro-Zeile — Vorjahres-Gesamtumsatz (BWA). */
  vjGesamtEur?: number | null;
  /** Jahresfortschritt 0..1 (Tag im Jahr) fuer Pace + InfraX-Mini-Strecke. */
  tagImJahrPct?: number;
  /** K7-Flow-Kaskade: Position in der Firmen-Reihe → gestaffelter Flash. */
  flowIndex?: number;
  loading?: boolean;
}

interface KpiPair {
  label: string;
  value: string;
}

function pickKpis(firma: FirmaSlug, data?: EntityKpi | null): KpiPair[] {
  if (!data) return [];
  const cash = data.saldo_eur ? formatEUR(data.saldo_eur) : "—";
  if (firma === "drainx") {
    return [
      { label: "Cash", value: cash },
      {
        label: "DSO",
        value: data.dso_days != null ? `${data.dso_days.toFixed(0)} T` : "—",
      },
    ];
  }
  if (firma === "gundk") {
    return [
      { label: "Cash", value: cash },
      {
        label: "Pipeline",
        value: data.pipeline_eur ? formatEUR(data.pipeline_eur) : "—",
      },
    ];
  }
  if (firma === "infrax") {
    return [];
  }
  // xholding
  return [
    { label: "Cash", value: cash },
    {
      label: "Ford. (Brutto)",
      value: data.forderungen_eur ? formatEUR(data.forderungen_eur) : "—",
    },
  ];
}

function parseEur(v: string | null | undefined): number | null {
  if (v == null || v === "") return null;
  const n = Number.parseFloat(v);
  return Number.isFinite(n) ? n : null;
}

export default function PerCompanyBentoTile({
  firma,
  data,
  umsatzYtdNettoEur,
  umsatz12m,
  frische,
  frischeHint,
  vjGesamtEur,
  tagImJahrPct,
  flowIndex = 0,
  loading,
}: Props) {
  const umsatzTarget = parseEur(umsatzYtdNettoEur);
  const animated = useCountUp(umsatzTarget);
  // K7: pulst 1×, wenn sich der Umsatz nach Refetch/Edit ändert — Firmen-
  // Reihe gestaffelt (Flow-Kaskade), startet nach der Zone-A-Welle.
  const umsatzFlash = useFlashOnChange(umsatzTarget);

  if (loading) {
    return <Skeleton firma={firma} />;
  }
  const palette = FIRMA_COLORS[firma];
  const kpis = pickKpis(firma, data);
  const hatIrgendwas = umsatzTarget != null || kpis.length > 0;
  const sparkData = umsatz12m ?? [];

  // AUFTRAG M B1: Pace als Mikro-Zeile — Umsatz vs. Vorjahr ("78 % VJ")
  // plus Abstand zum Tag-im-Jahr-Soll ("+28 pp"). Die Pace-Bar-Karten und
  // der Donut sind gestorben; DIES ist der eine Wohnort.
  let paceZeile: string | null = null;
  if (
    umsatzTarget != null &&
    vjGesamtEur != null &&
    vjGesamtEur > 0 &&
    tagImJahrPct != null &&
    tagImJahrPct > 0
  ) {
    const fill = umsatzTarget / vjGesamtEur;
    const deltaPp = Math.round((fill - tagImJahrPct) * 100);
    paceZeile = `${Math.round(fill * 100)} % VJ · ${deltaPp >= 0 ? "+" : "−"}${Math.abs(deltaPp)} pp`;
  }

  // InfraX: Mini-BahnStrecke ersetzt die Sparkline (M B1).
  const zeigeMiniStrecke = firma === "infrax" && tagImJahrPct != null;

  return (
    <div className="h-full flex flex-col justify-between gap-1.5">
      <header className="flex items-baseline justify-between gap-2">
        <div
          className={`flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-semibold ${palette.text}`}
        >
          <span className={`h-2 w-2 rounded-full ${palette.dot}`} aria-hidden />
          {FIRMA_LABEL[firma]}
        </div>
        <div className="flex items-center gap-1.5">
          {frische != null && (
            <span
              title={frischeHint ?? `Datenstand: ${frische}`}
              aria-label={`Datenfrische: ${frische}`}
              className={`h-1.5 w-1.5 rounded-full cursor-help ${FRISCHE_DOT[frische]}`}
            />
          )}
          <span className="text-[10px] text-slate-500">→</span>
        </div>
      </header>

      {/* EINE grosse echte Zahl: Umsatz YTD netto (Tooltime, taeglich) —
          dahinter die 12M-BWA-Sparkline einfarbig im Firmen-Hex. */}
      {umsatzTarget != null ? (
        <div className="flex-1 flex flex-col justify-center relative">
          {zeigeMiniStrecke ? (
            <div
              aria-hidden
              className="pointer-events-none absolute inset-x-0 bottom-0 opacity-60"
            >
              <BahnStrecke
                mini
                ytdEur={umsatzTarget}
                tagImJahrPct={tagImJahrPct ?? 0}
                jahr={new Date().getFullYear()}
                delayMs={200}
              />
            </div>
          ) : (
            sparkData.length >= 2 && (
              <div
                aria-hidden
                className="pointer-events-none absolute inset-x-0 bottom-0 opacity-50"
              >
                <Sparkline data={sparkData} hex={palette.hex} height={28} />
              </div>
            )
          )}
          <div
            className={`relative z-10 text-2xl xl:text-3xl font-bold tracking-tight tabular-nums text-slate-50 ${flashClass(umsatzFlash)}`}
            style={flashStyle(umsatzFlash, 280 + flowIndex * 70)}
          >
            {formatEurRound(animated)}
          </div>
          <div className="relative z-10 text-[10px] text-slate-400 mt-0.5">
            Umsatz YTD · netto
            {paceZeile && (
              <span className="num-mono text-slate-300"> · {paceZeile}</span>
            )}
          </div>
        </div>
      ) : !hatIrgendwas ? (
        <div className="flex-1 flex items-center text-xs text-slate-500">
          Daten anbinden
        </div>
      ) : null}

      {kpis.length > 0 && (
        <dl className="grid grid-cols-2 gap-2">
          {kpis.map((kpi) => (
            <div key={kpi.label} className="min-w-0">
              <dt className="meta-xs">{kpi.label}</dt>
              <dd className="mt-0.5 text-xs font-semibold tabular-nums text-slate-200 truncate">
                {kpi.value}
              </dd>
            </div>
          ))}
        </dl>
      )}

      <footer className="text-[10px] text-slate-500">
        {firma === "infrax" ? "Eigener Datenraum (R1) · Details →" : "Details öffnen →"}
      </footer>
    </div>
  );
}

function Skeleton({ firma }: { firma: FirmaSlug }) {
  const palette = FIRMA_COLORS[firma];
  return (
    <div className="h-full flex flex-col gap-2 animate-pulse">
      <div
        className={`text-[11px] uppercase tracking-widest font-semibold ${palette.text} opacity-70`}
      >
        {FIRMA_LABEL[firma]}
      </div>
      <div className="h-8 w-2/3 rounded bg-slate-700/40" />
      <div className="h-4 w-1/3 rounded bg-slate-700/40" />
    </div>
  );
}
